import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PlusOutline } from '@lyearn/icons';
import { Box, InfoPill } from '@lyearn/ui';
import Filter from '@/components/Filter';
import { FilterOption } from '@/components/Filter/types';
import { SkillItemRenderer } from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/CreateFeedbackModal/components/RelatedSkillsOrMindsets/utils';
import usePraiseRelatedSkills from '@/modules/praises/views/PraiseWall/hooks/usePraiseRelatedSkills';

import SelectedSkillPill from '../SelectedSkillPill';
import { MENU_LIST_OVERRIDES } from './styles';
import { PraiseRelatedSkillsProps } from './types';

export const CLASSES = { root: 'p-0', label: 'body-short-02' };

const EMPTY_ARRAY: never[] = [];

const PraiseRelatedSkills: React.FC<PraiseRelatedSkillsProps> = (props) => {
  const { value, receiverUsers, onChange } = props;
  const { t } = useTranslation(['Feedback']);

  const userIds = useMemo(() => receiverUsers.map((user) => user.id), [receiverUsers]);

  const {
    searchText,
    setSearchText,
    selectedSkills,
    filterGroups,
    open,
    toggle,
    onAddSkill,
    selectedSkillsState,
    onRemoveSkill,
  } = usePraiseRelatedSkills({
    userIds,
    skillRatings: value,
    onChange,
  });

  return (
    <Box className="flex flex-row flex-wrap">
      <Filter
        asyncOptions={{
          search: {
            placeholder: t('Praises:Form.SearchSkills'),
            value: searchText,
            onChange: setSearchText,
          },
        }}
        emptyLabel={
          userIds.length ? t('Feedback:NoSkillsDesc') : t('Feedback:NoUserSelectedSkillsDesc')
        }
        groups={filterGroups}
        itemRenderer={SkillItemRenderer}
        open={open}
        overrides={MENU_LIST_OVERRIDES}
        showActionButtons={false}
        value={(selectedSkillsState || EMPTY_ARRAY) as FilterOption[]}
        onChange={onAddSkill}
        onClose={toggle}
        onToggle={toggle}>
        <InfoPill
          Icon={PlusOutline}
          className="mb-8 me-8"
          label={t('Praises:Form.AddSkills')}
          shape="round"
          type="icon"
          variant="outlined"
        />
      </Filter>
      {selectedSkills?.map((skill) => (
        <SelectedSkillPill
          key={skill.id}
          label={skill.name}
          onRemove={() => onRemoveSkill(skill.id)}
        />
      ))}
    </Box>
  );
};

export default PraiseRelatedSkills;
